import * as THREE from 'three';
import { t } from './i18n';
import { bootstrapLanding, failLanding } from './setup/landing';
import { bootstrapHistory } from './session/historyView';
import { bootstrapLeaderboard } from './session/leaderboardView';
import { PausePanel } from './session/pausePanel';
import { SummaryPanel } from './session/summary';
import { SettingsPanel } from './session/settingsPanel';
import { SessionController } from './session/sessionController';
import { castFromController } from './ui/interactive';
import { disposeMenuPointer, initMenuPointer, updateMenuPointer } from './ui/pointer';
import { findControllerForHand, trackControllerHandedness } from './xr/controllers';
import { preloadUiAssets } from './ui/style';
import {
  createFloor,
  getEnvironmentDef,
  loadEnvironment,
  loadPassthroughEnvironment,
} from './scene/environment';
import { preloadSwordModel } from './sword/mesh';
import { updateDecals } from './feedback/decal';
import { getSetting, loadUserSettings } from './settings/userSettings';
import { armAudioOnFirstGesture, playUiClick } from './feedback/audioManager';
import { isFirebaseConfigured } from './firebase/config';
import {
  CAMERA_FAR_M,
  CAMERA_FOV_DEG,
  CAMERA_NEAR_M,
  SCENE_LIGHT_DIR_COLOR,
  SCENE_LIGHT_DIR_INTENSITY,
  SCENE_LIGHT_HEMI_GROUND,
  SCENE_LIGHT_HEMI_INTENSITY,
  SCENE_LIGHT_HEMI_SKY,
} from './config/constants';

loadUserSettings();
preloadUiAssets();
// Fetch the sword GLB at boot so the swap is invisible by session start;
// a failure here is retried by upgradeSwordModel at the next session.
void preloadSwordModel().catch((err: unknown) => {
  console.warn('[sword] model preload failed, will retry at session start', err);
});
// startXR handles its own failures via failLanding; void marks the deliberate fire-and-forget.
bootstrapLanding((btn, status) => void startXR(btn, status));
// Background music + audio context need a user gesture to start (browser autoplay policy).
armAudioOnFirstGesture();
// Universal UI click on the 2D landing: any button beeps (menu, env, login, history…).
// The in-VR panels fire their own click from Panel.onPick.
document.addEventListener('click', (e) => {
  const el = e.target;
  if (el instanceof Element && el.closest('button')) playUiClick();
});
// 2D session history (reads the local/synced session log; repaints on cloud import).
// Isolated: this non-critical panel must never abort the module and block the
// auth/sync bootstrap below — the landing login button lives in that path.
try {
  bootstrapHistory();
} catch (err) {
  console.warn('[history] render failed, continuing', err);
}
// 2D shared leaderboard (Sfida Phase 3, #36). Self-guards: hidden in local-only mode and
// reads Firestore lazily on open. Isolated like the history panel — never block the bootstrap.
try {
  bootstrapLeaderboard();
} catch (err) {
  console.warn('[leaderboard] render failed, continuing', err);
}
// Firebase (auth UI + cloud sync) is optional and heavy — load it lazily so the
// landing CTA never waits on it; in local-only mode the chunk is never fetched.
if (isFirebaseConfigured()) {
  void Promise.all([
    import('./firebase/auth'),
    import('./firebase/loginUi'),
    import('./firebase/sync'),
  ])
    .then(([{ ensureAnonymousAuth }, { bootstrapAuthUi }, { startCloudSync }]) => {
      // Anonymous-by-default identity so the shared leaderboard works without a login (#35).
      ensureAnonymousAuth();
      bootstrapAuthUi();
      startCloudSync();
    })
    .catch((err: unknown) => {
      console.warn('[firebase] lazy load failed, running local-only', err);
    });
}

async function startXR(btn: HTMLButtonElement, status: HTMLElement): Promise<void> {
  btn.disabled = true;
  status.textContent = '';
  status.classList.remove('error');

  const { scene, camera, renderer, playerRig, controllerA, controllerB, onResize } = createScene();
  document.body.appendChild(renderer.domElement);

  // Passthrough ("Realtà") needs an AR session so the headset shows the real room; every
  // other environment is a fully virtual immersive-vr scene.
  const passthrough = getEnvironmentDef(getSetting('environment')).passthrough === true;
  let session: XRSession;
  try {
    session = await navigator.xr!.requestSession(passthrough ? 'immersive-ar' : 'immersive-vr', {
      optionalFeatures: ['local-floor', 'bounded-floor'],
    });
  } catch (err) {
    failLanding(btn, status, `${t('webxrNotSupported')} (${(err as Error).message})`);
    renderer.dispose();
    renderer.domElement.remove();
    return;
  }

  try {
    await renderer.xr.setSession(session);
  } catch (err) {
    // Renderer never bound, but the immersive session is live — end it (best-effort) to
    // release the headset, then mirror the requestSession-failure cleanup.
    void session.end().catch(() => {});
    failLanding(btn, status, `${t('webxrNotSupported')} (${(err as Error).message})`);
    renderer.dispose();
    renderer.domElement.remove();
    return;
  }

  // Global selectstart -> panel picker (works for wizard, pause, summary). Resolve the
  // firing controller by handedness, not by session.inputSources index: the two can disagree
  // (see trackControllerHandedness), which would otherwise cast the laser from the wrong hand.
  const onGlobalSelect = (e: XRInputSourceEvent): void => {
    castFromController(
      findControllerForHand(e.inputSource.handedness, controllerA, controllerB),
      scene,
    );
  };
  session.addEventListener('selectstart', onGlobalSelect);
  window.addEventListener('resize', onResize);

  // Quill menu pointer (rests on the panel, replaces the laser); panels acquire/release it.
  initMenuPointer(scene, controllerA, controllerB);

  const pausePanel = new PausePanel(scene, camera);
  const summaryPanel = new SummaryPanel(scene, camera);
  const settingsPanel = new SettingsPanel(scene, camera);

  const controller = new SessionController({
    scene,
    camera,
    session,
    controllerA,
    controllerB,
    pausePanel,
    summaryPanel,
    settingsPanel,
  });

  const clock = new THREE.Clock();
  renderer.setAnimationLoop(() => {
    controller.tickFrame(clock.getDelta(), playerRig);
    // Drive hit-marker fade/removal here (NOT via requestAnimationFrame, which is
    // paused inside immersive WebXR sessions — markers would never expire on Quest).
    updateDecals();
    updateMenuPointer(camera);
    renderer.render(scene, camera);
  });

  session.addEventListener('end', () => {
    renderer.setAnimationLoop(null);
    session.removeEventListener('selectstart', onGlobalSelect);
    window.removeEventListener('resize', onResize);
    disposeMenuPointer();
    controller.dispose();
    renderer.dispose();
    renderer.domElement.remove();
    btn.disabled = false;
  });

  controller.start();
}

type SceneBundle = {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  playerRig: THREE.Group;
  controllerA: THREE.Object3D;
  controllerB: THREE.Object3D;
  onResize: () => void;
};

function createScene(): SceneBundle {
  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(
    CAMERA_FOV_DEG,
    window.innerWidth / window.innerHeight,
    CAMERA_NEAR_M,
    CAMERA_FAR_M,
  );

  // `alpha: true` gives the XR framebuffer a transparent clear, so in a passthrough
  // (immersive-ar) session the real surroundings show through where nothing is drawn.
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.xr.enabled = true;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;

  scene.add(
    new THREE.HemisphereLight(
      SCENE_LIGHT_HEMI_SKY,
      SCENE_LIGHT_HEMI_GROUND,
      SCENE_LIGHT_HEMI_INTENSITY,
    ),
  );
  const dir = new THREE.DirectionalLight(SCENE_LIGHT_DIR_COLOR, SCENE_LIGHT_DIR_INTENSITY);
  dir.position.set(2, 5, 2);
  scene.add(dir);

  // Resolved at scene creation (every "Enter VR"), so only the selected
  // environment's assets are ever fetched.
  const env = getEnvironmentDef(getSetting('environment'));
  if (env.passthrough) {
    // No floor/hall: the immersive-ar session (see startXR) shows the real room; we render
    // only the blade, targets and UI over it.
    void loadPassthroughEnvironment(scene, renderer, env);
  } else {
    const floor = createFloor(env);
    scene.add(floor);
    void loadEnvironment(scene, renderer, floor, env);
  }

  const controllerA = renderer.xr.getController(0);
  const controllerB = renderer.xr.getController(1);
  // Keep each controller's bound handedness on its userData so hand→controller lookups stay
  // correct across a Meta-button suspend/resume (Three.js may re-bind the slots).
  trackControllerHandedness(controllerA);
  trackControllerHandedness(controllerB);

  const playerRig = new THREE.Group();
  playerRig.add(camera);
  playerRig.add(controllerA);
  playerRig.add(controllerB);
  scene.add(playerRig);

  const onResize = (): void => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  };

  return { scene, camera, renderer, playerRig, controllerA, controllerB, onResize };
}
